import { useMemo } from "react";

import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { SelectedOrganizationIdGenerator } from "@/Common/Api/MedfarDictionaries/Dto/Responses/SelectedOrganizationIdGenerator";
import { SelectControl } from "@/Common/Components/Inputs/SelectControl";
import { FilteredOrganizationRules } from "@/Common/Utilities/FilteredOrganizationRules";

interface IOrganizationComboBoxProps {
    filteredOrganizationRules: FilteredOrganizationRules;
    selectedOrganizationIdentifier: string;
    onOrganizationSelect: (organizationIdentifier: string) => void;
    isRequired?: boolean;
    isValid: boolean;
}

function OrganizationComboBox(props: IOrganizationComboBoxProps): JSX.Element {
    const optionsByState = useMemo(
        () => {
            const countryCodes = props.filteredOrganizationRules.getCountryCodes();

            return countryCodes.flatMap(countryCode => {
                const stateCodes = props.filteredOrganizationRules.getStateCodes(countryCode);

                return stateCodes.map(stateCode => {
                    const stateName = props.filteredOrganizationRules.getStateLabel(countryCode, stateCode);

                    return {
                        label: countryCodes.length === 1
                            ? stateName
                            : `${props.filteredOrganizationRules.getCountryLabel(countryCode)} - ${stateName}`,
                        options: props.filteredOrganizationRules
                            .getDistinctOrganizations(countryCode, stateCode)
                            .map(p => ({
                                label: LicenceOrganisationRule.getOrganizationAcronymAndName(p),
                                value: SelectedOrganizationIdGenerator.generateId(p)
                            }))
                    };
                });
            });
        },
        [props.filteredOrganizationRules]
    );

    const options = [
        ...props.filteredOrganizationRules.getNoOrganizationTitles().any()
            ? [{ label: SelectedOrganizationIdGenerator.noOrganizationCode, value: SelectedOrganizationIdGenerator.noOrganizationCode }]
            : [],
        ...optionsByState
    ];

    return (
        <SelectControl
            options={options}
            useEmptyOption
            value={props.selectedOrganizationIdentifier}
            isInvalid={!props.isValid}
            onChange={organizationId => props.onOrganizationSelect(organizationId)}
            data-cy="userProfile-practiceOrganization"
            isClearable
            isSearchable
        />
    );
}

export { OrganizationComboBox };
